import type { Booking } from "./services/bookings";

export function money(amount: number) {
    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'GBP',
    }).format(amount);
}

/**
 * Coerce API decimal/string values to a finite number (defaults to 0).
 */
export function toAmount(value: unknown): number {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
}

/** AT platform fee (net + VAT), matching backend application fee calculation. */
export function getAtServiceCharge(pricing?: Booking["pricing"]): number {
    const atFeeNet = toAmount(pricing?.at_fee_net ?? pricing?.service_charge);
    const atVat = toAmount(pricing?.at_vat_amount);

    return atFeeNet + atVat;
}

type DriverVatStatus =
    | "VAT_REGISTERED"
    | "NOT_VAT_REGISTERED"
    | "REVERSE_CHARGE_REVIEW"
    | "UNKNOWN_BLOCKED";

/**
 * Customer-facing booking totals assume a VAT-registered operator at estimate time.
 * Non-VAT drivers should not see or accept operator VAT in the offered price.
 */
export function getDriverOfferPrice(
    grossTotal: number,
    vatStatus: DriverVatStatus | string | null | undefined,
    operatorVatAmount?: unknown,
): number {
    if (vatStatus === "VAT_REGISTERED") {
        return grossTotal;
    }

    const operatorVat = toAmount(operatorVatAmount);
    if (operatorVat > 0) {
        return Math.max(0, grossTotal - operatorVat);
    }

    return grossTotal;
}

/**
 * Normalizes a UK driving license for storage/validation.
 * Strips spaces/hyphens and uppercases.
 */
export function normalizeUKLicense(license: string): string {
    return license.replace(/[\s-]+/g, "").toUpperCase();
}

/**
 * Validates UK driving license format
 * @param {string} license - The license number
 * @returns {boolean} - True if valid format
 */
export function validateUKLicense(license: string): boolean {
    // 5 surname chars + 6 DOB digits + 2 initials + 3 issue/check chars
    const ukRegex = /^[A-Z9]{5}[0-9]{6}[A-Z9]{2}[A-Z0-9]{3}$/;

    return ukRegex.test(normalizeUKLicense(license));
}